import { useContext } from 'react';

import {
    ActionApi,
    AttachmentApi,
    CategorieRejetApi,
    CodeRejetApi,
    DefFrequenceApi,
    ExceptionFacturanteApi,
    ExceptionRejetApi,
    FactureAchatApi,
    FactureAchatCodeRejetApi,
    FactureAchatJalApi,
    FactureAchatLigneApi,
    FactureAchatNoteApi,
    MediaObjectApi,
    MediaObjectNoteApi,
    ReleveAchatApi,
    ReminderPublisherApi,
    WorkflowMailApi,
} from '@europrocurement/l2d-domain/openApi/ApiAchats';
import {
    AchatsReducerType,
    CodeRejetApiObject,
    FactureAchatApiObject,
    MediaObjectApiObject,
    ReminderPublisherApiObject,
} from '@europrocurement/l2d-domain/reducers';

import { DataSourcesThunksType, getDataThunkType } from '@europrocurement/l2d-redux-utils';
import { ExceptionFacturanteApiObject } from '@europrocurement/l2d-domain/reducers/achats/slices/exceptionFacturante';
import { ReleveAchatApiObject } from '@europrocurement/l2d-domain/reducers/achats/slices/invoiceStatementSlice';
import L2DApiContext from '../L2DApiContext';

// Enum to map view names to corresponding camelCase function names
export enum ViewFunctionMap {
    unregistered = 'unregisteredInvoices',
    user_unregistered = 'userUnregisteredInvoices',
    unassigned_unregistered = 'unassignedUnregisteredInvoices',
}

export type ViewName = keyof typeof ViewFunctionMap;

// Utility function to get ViewName key from a ViewFunctionMap value
export function getViewNameFromValue(value: string): ViewName | undefined {
    return (Object.keys(ViewFunctionMap) as Array<ViewName>).find(
        (key) => ViewFunctionMap[key] === value,
    );
}

export const getFromSpecifiedDataSourceThunks = <T, A extends string>(
    dataSourcesThunks: DataSourcesThunksType<T>,
    dataSourceName: A,
) => dataSourcesThunks[dataSourceName].getData;

/**
 * !! Currently not generic because ViewFunctionMap and ViewName are strongly associated to MediaObject !!
 */
export const getSpecifiedDataSourceByViewName = <T, A extends ViewName>(
    dataSourcesThunks: DataSourcesThunksType<T>,
    dataSourceNameFromView: A,
) =>
    getFromSpecifiedDataSourceThunks(
        dataSourcesThunks,
        `${ViewFunctionMap[dataSourceNameFromView]}`,
    );

export const useAchatsService = () => {
    const context = useContext(L2DApiContext);
    if (!context) {
        throw new Error('useL2DApi must be used within a L2DApiProvider');
    }

    if (!context.achats) {
        throw new Error('Achat service is not registred in L2DApiProvider');
    }

    const { apis, slices, thunks, fetchItems, getItems, reducer } = context.achats;

    const getMediaObjectByViewName = <T extends ViewName>(dataSourceName: T) =>
        getSpecifiedDataSourceByViewName(thunks.mediaobjectDataSourcesThunks, dataSourceName);

    return {
        // [ACHATS] Apis

        actionApi: apis.actionApi as ActionApi,
        attachmentApi: apis.attachmentApi as AttachmentApi,
        categorieRejetApi: apis.categorieRejetApi as CategorieRejetApi,
        codeRejetApi: apis.codeRejetApi as CodeRejetApi,
        defFrequenceApi: apis.defFrequenceApi as DefFrequenceApi,
        exceptionFacturanteApi: apis.exceptionFacturanteApi as ExceptionFacturanteApi,
        exceptionRejetApi: apis.exceptionRejetApi as ExceptionRejetApi,
        factureAchatApi: apis.factureAchatApi as FactureAchatApi,
        factureAchatCodeRejetApi: apis.factureAchatCodeRejetApi as FactureAchatCodeRejetApi,
        factureAchatJalApi: apis.factureAchatJalApi as FactureAchatJalApi,
        factureAchatLigneApi: apis.factureAchatLigneApi as FactureAchatLigneApi,
        factureAchatNoteApi: apis.factureAchatNoteApi as FactureAchatNoteApi,
        mediaObjectApi: apis.mediaObjectApi as MediaObjectApi,
        mediaObjectNoteApi: apis.mediaObjectNoteApi as MediaObjectNoteApi,
        releveAchatApi: apis.releveAchatApi as ReleveAchatApi,
        reminderPublisherApi: apis.reminderPublisherApi as ReminderPublisherApi,
        workflowMailApi: apis.workflowMailApi as WorkflowMailApi,

        // [ACHATS] Slices

        categorieStatsSlice: slices.categorieStatsSlice,
        codeRejetSlice: slices.codeRejetSlice,
        codeRejetStatsSlice: slices.codeRejetStatsSlice,
        defFrequenceSlice: slices.defFrequenceSlice,
        exceptionFacturanteSlice: slices.exceptionFacturanteSlice,
        factureAchatSlice: slices.factureAchatSlice,
        invoiceStatementSlice: slices.invoiceStatementSlice,
        mediaobjectSlice: slices.mediaobjectSlice,
        reminderPublisherSlice: slices.reminderPublisherSlice,

        // [ACHATS] Thunks
        categorieStatsDataSourcesThunks: thunks.categorieStatsDataSourcesThunks,
        codeRejetDataSourcesThunks: thunks.codeRejetDataSourcesThunks,
        codeRejetStatsDataSourcesThunks: thunks.codeRejetStatsDataSourcesThunks,
        defFrequenceDataSourcesThunks: thunks.defFrequenceDataSourcesThunks,
        exceptionFacturanteDataSourcesThunks: thunks.exceptionFacturanteDataSourcesThunks,
        factureAchatDataSourcesThunks: thunks.factureAchatDataSourcesThunks,
        invoiceStatementDataSourcesThunks: thunks.invoiceStatementDataSourcesThunks,
        mediaobjectDataSourcesThunks:
            thunks.mediaobjectDataSourcesThunks as DataSourcesThunksType<MediaObjectApiObject>,
        reminderPublisherDataSourcesThunks: thunks.reminderPublisherDataSourcesThunks,

        // [ACHATS] Actions - Fetch data
        getCategorieStats: fetchItems.getCategorieStats,
        getCodeRejet: fetchItems.getCodeRejet as getDataThunkType<CodeRejetApiObject>,
        getCodeRejetStats: fetchItems.getCodeRejetStats,
        getDefFrequence: fetchItems.getDefFrequence,
        getExceptionFacturante:
            fetchItems.getExceptionFacturante as getDataThunkType<ExceptionFacturanteApiObject>,
        getFactureAchat: fetchItems.getFactureAchat as getDataThunkType<FactureAchatApiObject>,
        getInvoiceStatement:
            fetchItems.getInvoiceStatement as getDataThunkType<ReleveAchatApiObject>,
        getMediaobject: fetchItems.getMediaobject as getDataThunkType<MediaObjectApiObject>,
        getUnregisteredMediaobject:
            fetchItems.getUnregisteredMediaobject as getDataThunkType<MediaObjectApiObject>,
        getUserUnregisteredMediaobject:
            fetchItems.getUserUnregisteredMediaobject as getDataThunkType<MediaObjectApiObject>,
        getUnassignedMediaobject:
            fetchItems.getUnassignedMediaobject as getDataThunkType<MediaObjectApiObject>,
        getReminderPublisher:
            fetchItems.getReminderPublisher as getDataThunkType<ReminderPublisherApiObject>,
        getCodeRejetFournisseur: fetchItems.getCodeRejetFournisseur,
        getFactureAchatFromReminderPublisher:
            fetchItems.getFactureAchatFromReminderPublisher as getDataThunkType<FactureAchatApiObject>,
        getFactureAchatFromStatement:
            fetchItems.getFactureAchatFromStatement as getDataThunkType<FactureAchatApiObject>,
        getFactureAchatPause:
            fetchItems.getFactureAchatPause as getDataThunkType<FactureAchatApiObject>,
        getFactureAchatStats:
            fetchItems.getFactureAchatStats as getDataThunkType<FactureAchatApiObject>,

        // [ACHATS] Actions - select item
        selectCategorieStats: getItems.selectCategorieStats,
        selectCodeRejet: getItems.selectCodeRejet,
        selectCodeRejetStats: getItems.selectCodeRejetStats,
        selectDefFrequence: getItems.selectDefFrequence,
        selectExceptionFacturante: getItems.selectExceptionFacturante,
        selectFactureAchat: getItems.selectFactureAchat,
        selectInvoiceStatement: getItems.selectInvoiceStatement,
        selectMediaobject: getItems.selectMediaobject,
        selectReminderPublisher: getItems.selectReminderPublisher,
        selectCodeRejetFournisseur: getItems.selectCodeRejetFournisseur,
        selectFactureAchatFromReminderPublisher: getItems.selectFactureAchatFromReminderPublisher,
        selectFactureAchatFromStatement: getItems.selectFactureAchatFromStatement,
        selectFactureAchatPause: getItems.selectFactureAchatPause,
        selectFactureAchatStats: getItems.selectFactureAchatStats,

        // [ACHATS] Reducer
        reducer: reducer as AchatsReducerType,

        // [ACHATS] other

        getMediaObjectByViewName,
    };
};
